import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular';

import { FullCalendarComponent } from '@fullcalendar/angular';
import { Calendar, CalendarOptions, EventClickArg } from '@fullcalendar/core';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import timeGridPlugin from '@fullcalendar/timegrid';

import { getDateWithTimezone, getFormattedDateForCalendarTittle, getFormattedShortDate, getformattedShortStartEndDate, getNextWeekendDay } from '../../../utils/dates';
import { CurbUser, Listing, OpenHouseEvent, User_global } from '../../app.models';
import { OpenHouseSchedingService } from '../../services/open-house-scheduling.service';
import {
  OpenHouseEventStatus,
  EventApiTyped,
  DateWithTimezone,
  AdminOpenHouseModalData,
  AdminOpenHouseModalOutput,
  AgentOpenHouseModalData,
  AgentOpenHouseModalOutput,
  WithId,
  CreateUpdateEventPayload,
} from '../../types';
import { OpenHouseSchedulerNewEvent } from '../open-house-schedule-new-event/open-house-schedule-new-event';

import { UtilsService } from '../../services/utils.service';
import { AuthService } from '../../services/auth.service';

import { CommonProvider } from '../../services/common';
import { OpenHouseSchedulePreview } from '../open-house-schedule-preview/open-house-schedule-preview';
import { DEFAULT_OPEN_HOUSE_END_TIME, DEFAULT_OPEN_HOUSE_START_TIME, DEFAULT_TIMEZONE, TIMEZONES } from '../../constants';
import { NetworkProvider } from '../../services/network';
import moment from 'moment-timezone';

@Component({
  selector: 'open-house-scheduler',
  templateUrl: './open-house-scheduler.html',
  styleUrls: ['./open-house-scheduler.scss'],
  standalone: false,
})
export class OpenHouseScheduler implements AfterViewInit {
  TIMEZONES = TIMEZONES.map((x) => ({ value: x.value, display: `${x.display} UTC${moment().tz(x.value).format('Z')}` }));
  User_global = User_global;

  currentUser: WithId<CurbUser>;
  selectedTimezone = DEFAULT_TIMEZONE;

  @Input() mode: 'agent' | 'admin' = 'admin';

  // Only needed for the admin mode
  @Input() listing: Listing;

  @Output() onEventsChange = new EventEmitter<EventApiTyped[]>();
  // The fullcalendar instance initialization doesn't happen syncronously, so you should listen to this event to know
  // the calendar is ready to be used.
  @Output() onSchedulerInitialized = new EventEmitter<void>();

  calendarOptions: CalendarOptions = {
    timeZone: this.selectedTimezone,
    plugins: [timeGridPlugin, interactionPlugin, momentTimezonePlugin],
    initialView: 'main',
    headerToolbar: {
      start: 'prev',
      center: 'title',
      end: 'next',
    },
    // TODO: Pass in the timezone. Also, when this one changes, manually retrigger this fn
    titleFormat: (args) => getFormattedDateForCalendarTittle(args.start.marker, args.end.marker),
    datesSet: () => this.readjustMinAndMaxTimeSlot(),
    views: {
      main: {
        type: 'timeGrid',
        duration: { weeks: 1 },
        allDaySlot: false,
        // TODO: THIS MAY INtroduce flicker if the loaded events are less that the min slot time
        slotMinTime: `${DEFAULT_OPEN_HOUSE_START_TIME}:00:00`,
        slotMaxTime: `${DEFAULT_OPEN_HOUSE_END_TIME}:00:00`,
      },
    },

    // Blocked by: https://github.com/fullcalendar/fullcalendar/issues/7652
    nowIndicator: false,

    // Calendar size is controlled from the parent div via scss
    height: '100%',
    dateClick: this.handleDateClick.bind(this),
    eventClick: this.handleEventClick.bind(this),
    // Note: This will trigger each time the change data
    eventsSet: (events) => {
      this.events = events as EventApiTyped[];
    },
    // Will trigger when fullcalendar initializes
    viewDidMount: () => {
      this.onSchedulerInitialized.emit();
    },
  };

  // Safety flag used to ensure that the only one modal is open at a time
  modalOpen = false;

  @ViewChild('calendar') calendarComponent: FullCalendarComponent;

  events: EventApiTyped[] = [];

  calendarApi: Calendar;

  OpenHouseEventStatus = OpenHouseEventStatus;

  constructor(
    private auth: AuthService,
    private utilsService: UtilsService,
    private modalCtrl: ModalController,
    private common: CommonProvider,
    private network: NetworkProvider,
    private openHouseSchedulingService: OpenHouseSchedingService
  ) {}

  async ngAfterViewInit() {
    this.calendarApi = this.calendarComponent.getApi();

    this.currentUser = await this.auth.getCurrentFullUser();
    this.selectedTimezone = this.currentUser.timezone || DEFAULT_TIMEZONE;

    const userTimezoneInKnownTimezones = this.TIMEZONES.findIndex((x) => x.value === this.selectedTimezone);

    // If the user's timezone is not known, we include it first in the list
    if (userTimezoneInKnownTimezones === -1) {
      const offset = getDateWithTimezone(new Date(), this.selectedTimezone).format('Z');
      const display = this.selectedTimezone.replace('_', ' ');
      this.TIMEZONES.unshift({ display: `${display} UTC${offset}`, value: this.selectedTimezone });
    }

    this.updateTimezone(this.selectedTimezone);
  }

  loadEvents(events: OpenHouseEvent[]) {
    if (!this.calendarComponent) {
      this.auth.logErrors(`"calendarComponent" wasn't present when loading events`);
      return;
    }

    this.calendarApi.removeAllEvents();
    this.events = [];

    // this.calendarApi.addEventSource({ events: events.map(x => ({ id: x.eventId, ...x }))})

    events.map((x) => this.calendarApi.addEvent({ id: x.eventId, ...x }));

    this.updateUI();
  }

  removeEventById(eventId: string) {
    const event = this.calendarApi.getEventById(eventId);

    if (event) {
      event.remove();
      this.updateUI();
    }
  }

  // Tasks that we need to run after an events gets added / removed / updated
  private updateUI() {
    this.readjustMinAndMaxTimeSlot();
    this.onEventsChange.emit(this.events);
  }

  // Cliking in a empty space
  async handleDateClick(event?: DateClickArg) {
    if (this.modalOpen || this.mode === 'agent') {
      return;
    }

    if (!this.network.checkInternet()) {
      await this.network.noNetworkModal();
      return;
    }

    this.modalOpen = true;

    let start: DateWithTimezone;
    let end: DateWithTimezone;

    // No event means that the method was called from the "Add time button"
    if (!event) {
      const nextWeekendDay = getNextWeekendDay(this.selectedTimezone);

      start = nextWeekendDay.startOf('day').hour(DEFAULT_OPEN_HOUSE_START_TIME) as DateWithTimezone;
      end = nextWeekendDay.clone().startOf('day').hour(DEFAULT_OPEN_HOUSE_END_TIME) as DateWithTimezone;
    } else {
      start = getDateWithTimezone(event.date, this.selectedTimezone);
      end = start.clone().add(1, 'hour') as DateWithTimezone;
    }

    const data = await this._openAdminModal({
      status: OpenHouseEventStatus.free,
      requiresApproval: false,
      start,
      end,
      listingAddress: this.listing?.address?.title,
      internalNote: '',
    });

    this.modalOpen = false;

    // Modal dismissed
    if (!data) {
      return;
    }

    await this._createEvent(data);
  }

  // Clicking on an existing event
  private async handleEventClick(rawEvent: EventClickArg) {
    if (this.modalOpen) {
      return;
    }

    if (!this.network.checkInternet()) {
      await this.network.noNetworkModal();
      return;
    }

    this.modalOpen = true;

    const event = rawEvent.event as EventApiTyped;
    const { start: _start, end: _end, extendedProps } = event;

    const start = getDateWithTimezone(_start, this.selectedTimezone);
    const end = getDateWithTimezone(_end, this.selectedTimezone);

    if (this.mode === 'agent') {
      const agent = { ...(User_global as any), objectID: extendedProps.agentId };

      const payload: AgentOpenHouseModalData = {
        eventId: extendedProps.eventId,

        status: extendedProps.status,
        agent,

        start,
        end,

        internalNote: extendedProps.internalNote || '',
        requiresApproval: extendedProps.requiresApproval,
        didAgentRequestEvent: extendedProps.didAgentRequestEvent,

        listingAddress: extendedProps.listingAddress,
        formattedShortDate: extendedProps.formattedShortDate,

        agentImageURL: extendedProps.agentImageURL,
        agentAvatarName: extendedProps.agentAvatarName,
      };

      const data = await this._openAgentModal(payload, this.currentUser);

      this.modalOpen = false;

      // Modal dismissed
      if (!data) {
        return;
      }

      return this._requestEvent(event, { ...payload, ...data });
    }

    const eventModalData: AdminOpenHouseModalData = {
      status: extendedProps.status,
      requiresApproval: extendedProps.requiresApproval,

      start,
      end,

      listingAddress: extendedProps.listingAddress,
      internalNote: extendedProps.internalNote || '',

      agentRequests: extendedProps.agentRequests || [],

      eventId: extendedProps.eventId,
      agent: extendedProps.agent,

      agentAvatarName: extendedProps.agentAvatarName,
      agentImageURL: extendedProps.agentImageURL,

      assignEventFn: this._assignEvent.bind(this, event),
    };

    const data = await this._openAdminModal(eventModalData);
    this.modalOpen = false;

    // Modal dismissed
    if (!data) {
      return;
    }

    if (data.action == 'delete') {
      await this._deleteEvent(event);
    } else {
      await this._updateEvent(event, data);
    }
  }

  private _handleAgentOpenModal(eventData: AgentOpenHouseModalData) {}

  private _handleAdminOpenModal(eventData: AdminOpenHouseModalData) {}

  private async _openAdminModal(eventData: AdminOpenHouseModalData): Promise<AdminOpenHouseModalOutput> {
    const modal = await this.modalCtrl.create({
      cssClass: ['new-modal-look', 'admin-creates-open-house-event-modal'],
      component: OpenHouseSchedulerNewEvent,
      componentProps: {
        eventData,
      },
    });

    await modal.present();

    const { data } = await modal.onDidDismiss<AdminOpenHouseModalOutput>();

    return data;
  }

  private async _openAgentModal(eventData: AgentOpenHouseModalData, currentUser: CurbUser): Promise<AgentOpenHouseModalOutput> {
    const modal = await this.modalCtrl.create({
      cssClass: ['new-modal-look', 'agent-request-open-house-event-modal'],
      component: OpenHouseSchedulePreview,
      componentProps: {
        eventData,
        currentUser,
      },
    });

    await modal.present();

    const { data } = await modal.onDidDismiss<AgentOpenHouseModalOutput>();

    return data;
  }

  private async _createEvent(newEventData: AdminOpenHouseModalOutput) {
    const { start, end, agent, status } = newEventData;

    const isFreeEvent = status === OpenHouseEventStatus.free;

    const now = getDateWithTimezone(new Date(), this.selectedTimezone);

    const [eventId] = await this.auth.generateNewDocIds(1);

    // NOTE: Every field added here needs its counterpart in the update method
    const newEvent: OpenHouseEvent = {
      // This will be setup after the event is created
      agentListingId: '',
      createdBy: '',
      adminId: this.currentUser.id,

      eventId,

      start: start.toDate(),
      end: end.toDate(),
      isPending: now.isBefore(end),
      classNames: isFreeEvent ? 'free-event' : 'assigned-event',
      color: this.utilsService.getOpenHouseEventColor(this.listing.address.title, agent),
      // Custom metadata will be accessible in the `extendendProps` field later
      // @TODO: Implement this using a table so that we don't consume more memory than needed
      agent,
      agentId: isFreeEvent ? undefined : agent.objectID,
      status,
      requiresApproval: newEventData.requiresApproval,
      internalNote: newEventData.internalNote || '',

      // We precompute the formatted string for perfomance reasons
      formattedShortDate: getFormattedShortDate(newEventData.start),
      formattedShortStartEndDate: getformattedShortStartEndDate(newEventData.start, newEventData.end),
      agentImageURL: isFreeEvent ? undefined : await this.common.getAgentProfileImageURL(agent.objectID),
      agentAvatarName: isFreeEvent ? undefined : this.auth.createAvatarName(agent.username),
      listingId: this.listing.id,
      listingAddress: this.listing.address.title,
    };

    // @ts-ignore This `id` is used internally by the calendar
    newEvent.id = eventId;

    this.calendarApi.addEvent(newEvent);
    this.updateUI();

    const createdEvent = await this.openHouseSchedulingService.createEvent(newEvent);
    if (!createdEvent.ok) {
      this.removeEventById(eventId);
      this.common.toast('Failed to create event');
    }
  }

  private async _deleteEvent(event: EventApiTyped) {
    // Optimistically update the UI
    event.remove();
    this.updateUI();

    const deletedEvent = await this.openHouseSchedulingService.deleteOpenHouseEvent(event.extendedProps.eventId, event.extendedProps.listingId);
    if (!deletedEvent.ok) {
      this.common.toast('Failed to delete event');
      this.calendarApi.addEvent(event);
      this.updateUI();
    }
  }

  private async _updateEvent(eventToUpdate: EventApiTyped, update: AdminOpenHouseModalData) {
    const eventCopy = eventToUpdate.toPlainObject() as EventApiTyped;

    const isFreeEvent = update.status === OpenHouseEventStatus.free;

    // NOTE: Every field added here needs its counterpart in the create method

    // Important, every time we update a field, the `eventSet` callback get's called

    eventToUpdate.setStart(update.start.toDate());
    eventToUpdate.setEnd(update.end.toDate());

    eventToUpdate.setProp('classNames', isFreeEvent ? 'free-event' : 'assigned-event');
    eventToUpdate.setProp('color', this.utilsService.getOpenHouseEventColor(eventToUpdate.extendedProps.listingAddress, update.agent));

    eventToUpdate.setExtendedProp('status', update.status);
    eventToUpdate.setExtendedProp('requiresApproval', update.requiresApproval);
    eventToUpdate.setExtendedProp('internalNote', update.internalNote);
    eventToUpdate.setExtendedProp('formattedShortDate', getFormattedShortDate(update.start));
    eventToUpdate.setExtendedProp('formattedShortStartEndDate', getformattedShortStartEndDate(update.start, update.end));

    if (isFreeEvent) {
      // Event is now free
      eventToUpdate.setExtendedProp('agent', undefined);
      eventToUpdate.setExtendedProp('agentId', undefined);
      eventToUpdate.setExtendedProp('agentImageURL', undefined);
      eventToUpdate.setExtendedProp('agentAvatarName', undefined);
    } else if (eventToUpdate.extendedProps.agent?.objectID !== update.agent?.objectID) {
      // Event changed agent
      eventToUpdate.setExtendedProp('agent', update.agent);
      eventToUpdate.setExtendedProp('agentId', update.agent.objectID);
      eventToUpdate.setExtendedProp('agentAvatarName', this.auth.createAvatarName(update.agent.username));
    } else {
      // Event is still the same agent, no updates needed
    }

    this.updateUI();

    const { eventId, status, requiresApproval, internalNote, listingId, agentId } = eventToUpdate.extendedProps;

    const updatePayload: CreateUpdateEventPayload = {
      start: eventToUpdate.start.getTime(),
      end: eventToUpdate.end.getTime(),
      eventId,
      status,
      requiresApproval,
      internalNote,
      listingId,
      agentId,
    };

    const updatedEvent = await this.openHouseSchedulingService.updateEvent(updatePayload, this.selectedTimezone);

    if (!updatedEvent.ok) {
      this.common.toast('Failed to update event');

      this.removeEventById(eventToUpdate.id);
      this.calendarApi.addEvent(eventCopy);
      this.updateUI();
    } else {
      eventToUpdate.setExtendedProp('agentRequests', updatedEvent.data.agentRequests);

      if (updatedEvent.data.status === OpenHouseEventStatus.assigned) {
        eventToUpdate.setExtendedProp('agentImageURL', updatedEvent.data.agentImageURL);
        eventToUpdate.setExtendedProp('agentListingId', updatedEvent.data.agentListingId);
      }
    }
  }

  // This method doesn't update the UI optimistically
  private async _assignEvent(eventToUpdate: EventApiTyped, assignedAgentId: string) {
    this.common.startLoading('Assigning event...');

    const { eventId, requiresApproval, internalNote, listingId } = eventToUpdate.extendedProps;

    const updatePayload: CreateUpdateEventPayload = {
      status: OpenHouseEventStatus.assigned,
      agentId: assignedAgentId,

      start: eventToUpdate.start.getTime(),
      end: eventToUpdate.end.getTime(),
      eventId,
      requiresApproval,
      internalNote,
      listingId,
    };

    const updatedEvent = await this.openHouseSchedulingService.updateEvent(updatePayload, this.selectedTimezone);

    let agentAvatarName = '';
    if (!updatedEvent.ok) {
      this.common.toast('Failed to update event');
    } else {
      const agent = updatedEvent.data.agent;

      eventToUpdate.setProp('classNames', 'assigned-event');
      eventToUpdate.setProp('color', this.utilsService.getOpenHouseEventColor(eventToUpdate.extendedProps.listingAddress, agent));

      eventToUpdate.setExtendedProp('status', OpenHouseEventStatus.assigned);

      eventToUpdate.setExtendedProp('agent', agent);
      eventToUpdate.setExtendedProp('agentId', agent.objectID);
      agentAvatarName = this.auth.createAvatarName(agent.username);
      eventToUpdate.setExtendedProp('agentAvatarName', agentAvatarName);
      eventToUpdate.setExtendedProp('agentImageURL', updatedEvent.data.agentImageURL);
      eventToUpdate.setExtendedProp('agentListingId', updatedEvent.data.agentListingId);
    }

    this.common.closeLoading();

    this.updateUI();

    return {
      status: OpenHouseEventStatus.assigned,
      agent: updatedEvent.data.agent,
      agentId: updatedEvent.data.agent.objectID,
      agentAvatarName: agentAvatarName,
      agentImageURL: updatedEvent.data.agentImageURL,
      agentListingId: updatedEvent.data.agentListingId,
    };
  }

  private async _requestEvent(eventToUpdate: EventApiTyped, update: AgentOpenHouseModalData & AgentOpenHouseModalOutput) {
    const eventCopy = eventToUpdate.toPlainObject() as EventApiTyped;

    if (update.requiresApproval) {
      eventToUpdate.setExtendedProp('didAgentRequestEvent', true);
    } else {
      eventToUpdate.setExtendedProp('status', OpenHouseEventStatus.assigned);
      eventToUpdate.setProp('classNames', 'assigned-event');
      eventToUpdate.setProp('color', this.utilsService.getOpenHouseEventColor(eventToUpdate.extendedProps.listingAddress, update.agent));
    }

    eventToUpdate.setExtendedProp('agent', update.agent);
    eventToUpdate.setExtendedProp('agentId', update.agent.objectID);
    eventToUpdate.setExtendedProp('agentAvatarName', this.auth.createAvatarName(update.agent.username));

    // Optimistically update the UI, this will reflect the color change of the event in the calendar and the min-max display
    this.updateUI();

    const updatedEvent = await this.openHouseSchedulingService.requestOpenHouseEvent(
      {
        eventId: eventToUpdate.extendedProps.eventId,
        listingId: eventToUpdate.extendedProps.listingId,
      },
      this.selectedTimezone
    );

    if (!updatedEvent.ok) {
      this.common.toast('Failed to request event');

      this.removeEventById(eventToUpdate.id);
      this.calendarApi.addEvent(eventCopy);
      this.updateUI();
    } else {
      eventToUpdate.setExtendedProp('agentImageURL', updatedEvent.data.agentImageURL);
    }

    // This second update is needed because from the backend we retrieve the "agentListingId" and we need to reupdate to update the chip in the listing detail
    this.updateUI();
  }

  private readjustMinAndMaxTimeSlot() {
    let min = DEFAULT_OPEN_HOUSE_START_TIME;
    let max = DEFAULT_OPEN_HOUSE_END_TIME;

    const calendarStartDay = this.calendarApi.view.activeStart;
    const calendarEndDay = this.calendarApi.view.activeEnd;

    for (const event of this.events) {
      const start = getDateWithTimezone(event.start, this.selectedTimezone);
      const end = getDateWithTimezone(event.end, this.selectedTimezone);

      // Adjust the min and max displayed hours based only on the events currently being displayed
      if (start.isAfter(calendarEndDay) || end.isBefore(calendarStartDay)) {
        continue;
      }

      if (start.hours() < min) {
        min = start.hours();
      }

      if (end.hours() + 1 > max) {
        max = end.hours() + 1;
      }
    }

    this.calendarApi.setOption('slotMinTime', `${min}:00:00`);
    this.calendarApi.setOption('slotMaxTime', `${max}:00:00`);
  }

  getPendingEvents(): { pendingFreeEvents: number; pendingAssignedEvents: number } {
    let pendingFreeEvents = 0;
    let pendingAssignedEvents = 0;

    const now = getDateWithTimezone(new Date(), this.selectedTimezone);

    for (const event of this.events) {
      const eventEndDate = getDateWithTimezone(event.end, this.selectedTimezone);
      const eventHasPassed = now.isAfter(eventEndDate);

      if (eventHasPassed) {
        continue;
      }

      const status = event.extendedProps.status;
      if (status === OpenHouseEventStatus.assigned) {
        pendingAssignedEvents++;
      } else if (status === OpenHouseEventStatus.free && !event.extendedProps.didAgentRequestEvent) {
        pendingFreeEvents++;
      }
    }

    return { pendingFreeEvents, pendingAssignedEvents };
  }

  async onTimezoneChange(timezone: string) {
    this.updateTimezone(timezone);

    await this.auth.updateUserDataProperty({ timezone });
    User_global.timezone = timezone;
  }

  updateTimezone(timezone: string) {
    this.selectedTimezone = timezone;

    // Since we store the date as string in the events, every time the timezone change we need to recompute those
    for (const event of this.events) {
      const start = getDateWithTimezone(event.start, timezone);
      const end = getDateWithTimezone(event.end, timezone);

      event.setExtendedProp('formattedShortDate', getFormattedShortDate(start));
      event.setExtendedProp('formattedShortStartEndDate', getformattedShortStartEndDate(start, end));
    }

    this.calendarApi.setOption('timeZone', timezone);
    this.calendarApi.render();

    this.updateUI();
  }

  logEvent(x: any) {
    console.error(x);
  }
}
